import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonPage,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import { chevronBack, closeOutline, refreshOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { addBot, updateAccount, updateBot } from "../lib/firebase";
import { Bot } from "../types/bot.type";
import { useBotsStore } from "../stores/bots.store";
import { useGlobalsStore } from "../stores/globals.store";
import { useLobbiesStore } from "../stores/lobbies.store";
import { useUsersStore } from "../stores/users.store";
import { BasicStrategy } from "../components/BasicStrategy";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import moment from "moment";
import { randomizeIcon } from "../utils/botUtils";
import ProfitIndicator from "../components/ProfitIndicator";
import BotAvatar from "../components/BotAvatar";
import ExchangeApiForm from "../components/ExchangeApiForm";
import AnimationContainer from "../components/Animation";

export const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
export const WEEK_IN_MILLISECONDS = 7 * DAY_IN_MILLISECONDS;

export const SetupBot: React.FC<{
  onDismiss: (bot?: any) => void;
  navigateTo?: (path: string) => void;
  mode: string;
  botId?: string;
  lobbyId?: string;
  questBot?: any;
}> = ({ onDismiss, navigateTo, mode, botId, lobbyId, questBot }) => {
  const user = useUsersStore(state => state.user);
  const defaultBot = useGlobalsStore(state => state?.globals?.defaultBotConfig);
  const botOnFile = useBotsStore(state => state.bots.find(b => b.id === botId));
  const firstPage = user?.hasSetupBot || botId ? (botOnFile?.name ? 3 : 2) : 1;
  const [page, setPage] = useState(firstPage);

  const endDateRef: any = new Date();
  const endDate = new Date(endDateRef.getTime() - DAY_IN_MILLISECONDS);
  const startDate = new Date(endDate.getTime() - WEEK_IN_MILLISECONDS);
  const [dateRange, setDateRange] = useState([
    {
      startDate,
      endDate,
      key: "selection"
    }
  ]);

  const [bot, setBot] = useState(
    (mode === "quest" && questBot) ||
      botOnFile ||
      ({
        img: randomizeIcon(),
        name: "",
        mode,
        provisioned: false
      } as Bot)
  );

  useEffect(() => {
    if (mode !== "quest") {
      setBot({
        ...bot,
        mode
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pageOrder = bot?.mode === "backtest" ? [0, 2, 3, 4] : [0, 1, 2, 3];

  const lobby = useLobbiesStore(
    state =>
      state.lobbies.find(l => l.id === lobbyId) ||
      state.openLobbies.find(l => l.id === lobbyId)
  );

  useEffect(() => {
    if (mode !== "quest" && !botId && defaultBot) {
      const newBot = {
        ...defaultBot,
        img: randomizeIcon(),
        name: "",
        mode,
        user: user?.userId,
        provisioned: false
      };
      setBot(lobbyId ? { ...newBot, lobby: lobbyId } : newBot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, defaultBot, user]);

  useEffect(() => {
    if (page === 0) {
      if (user && user.hasSetupBot) {
        setPage(2);
      } else {
        setPage(1);
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const createBot = async () => {
    if (!bot?.lobby) {
      bot.active = true;
      if (bot.mode === "backtest") {
        bot.backtestConfig = {
          startTime:
            moment(dateRange[0].startDate).format("YYYY-MM-DD") + " 00:00",
          endTime: moment(dateRange[0].endDate).format("YYYY-MM-DD") + " 00:00"
        };
      }
      if (bot?.mode === "live") {
        if (
          updateAccount &&
          user?.userId &&
          user?.binanceSecrets !== bot?.binanceSecrets
        ) {
          updateAccount(user?.userId, { binanceSecrets: bot?.binanceSecrets });
        }
      }
      const botPromise = addBot(bot);
      toast.promise(botPromise, {
        loading: "creating bot...",
        success: "Bot created",
        error: "Bot could not be created"
      });
      const response: any = await botPromise;
      const botId = response.key;
      navigateTo && navigateTo(`/bots/${botId}`);
      onDismiss();
    } else if (!lobby?.challenge) {
      onDismiss(bot);
      return;
    }
    if (user && !user.hasSetupBot) {
      updateAccount(user.userId, { hasSetupBot: true });
    }
    onDismiss();
  };

  const updateBotFn = async () => {
    if (mode === "quest") {
      await onDismiss(bot);
      return;
    }
    if (!botId) return;
    const updatedBot = { ...bot };
    delete updatedBot.data;
    const botPromise = updateBot(botId, updatedBot);
    toast.promise(botPromise, {
      loading: "updating bot...",
      success: "Bot updated",
      error: "Bot could not be updated"
    });
    await botPromise;
    onDismiss();
  };

  const apiFormSubmitFn = async (binanceSecrets: any) => {
    setBot({ ...bot, binanceSecrets });
  };

  return (
    <IonPage>
      <IonHeader className="py-2">
        <IonToolbar>
          <IonButtons slot="start">
            {page === 2 && (
              <IonButton fill="clear" onClick={() => onDismiss()}>
                <IonIcon icon={closeOutline} className="icon-only" />
              </IonButton>
            )}
            {page > 2 && (
              <IonButton
                className="text-sm"
                fill="clear"
                onClick={() => setPage(page - 1)}
              >
                <IonIcon icon={chevronBack} className="icon-only" />
              </IonButton>
            )}
          </IonButtons>

          <IonTitle className="font-semibold capitalize mx-auto">
            {`${botId ? "Edit" : "Create"} ${
              mode === "simulation" ? "simulated" : mode
            } Bot`}
          </IonTitle>
          {page > 1 && (
            <IonButtons slot="end">
              <IonButton
                className="text-sm font-semibold"
                fill="clear"
                onClick={() =>
                  botId || mode === "quest" ? updateBotFn() : createBot()
                }
              >
                {botId || mode === "quest" ? "Update" : "Create"} Bot
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={page === pageOrder[pageOrder.length - 1]}>
        <div className="flex flex-col items-center h-full w-full">
          <div className="w-full flex flex-col items-center px-6">
            {page === 1 && (
              <>
                <AnimationContainer animationName="botBuiltAnimation" />
                <p className="text-4xl font-semibold text-left my-16">
                  Let's set up your first Simulated Bot
                </p>
                <div className="text-lg">
                  Simulated Bots allow you to learn and experiment in real time
                  with
                  <b> Zero Risk</b>.
                  <p className="pt-5">
                    Complete your Bot profile, and earn
                    <br />
                    <b className="underline">10 QUO COIN to get you started </b>
                    in the process!
                  </p>
                </div>
              </>
            )}

            {page === 2 && (
              <>
                <IonAvatar className="bg-gray-200 w-32 h-32 mt-8">
                  <div className="relative">
                    <IonIcon
                      onClick={() => setBot({ ...bot, img: randomizeIcon() })}
                      icon={refreshOutline}
                      className="
                      h-7
                      w-7
                      rotate-[225deg]
                      flex
                      items-center
                      justify-center
                      text-white 
                      bg-[#303437]
                      rounded-full 
                      absolute 
                      right-1
                      p-1"
                    />
                  </div>
                  <IonImg src={bot?.img} />
                </IonAvatar>

                <p className="font-bold text-left text-2xl mt-7">
                  Give your Bot a name
                </p>

                <div className="w-full border-2 border-primary-50 rounded-[4px] mt-7">
                  <IonItem lines="none">
                    <IonInput
                      placeholder="Bot name"
                      value={bot?.name}
                      onIonChange={e =>
                        e.detail.value &&
                        setBot({ ...bot, name: e.detail.value })
                      }
                    />
                  </IonItem>
                </div>
                {bot?.mode === "live" && !bot?.provisioned && (
                  <div className="w-full border-2 border-primary-50 rounded-[4px] my-4 pb-4">
                    <ExchangeApiForm
                      onSubmit={apiFormSubmitFn}
                      binanceSecrets={bot?.binanceSecrets}
                    />
                  </div>
                )}
              </>
            )}

            {bot?.mode === "backtest" && page === 3 && !lobbyId && (
              <div className="bg-primary-50 rounded-lg mt-4">
                <div className="pt-4 px-4 pb-0 text-lg font-semibold">
                  Select Trade Period
                </div>
                <DateRange
                  className="m-1"
                  editableDateInputs={true}
                  minDate={new Date(2019, 0, 1)}
                  maxDate={endDate}
                  onChange={(item: any) => setDateRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={dateRange}
                />
              </div>
            )}
          </div>

          {((bot?.mode !== "backtest" && page === 3) ||
            (bot?.mode === "backtest" && !lobbyId && page === 4)) && (
            <>
              <div className="w-full flex bg-primary-90 p-4 items-center">
                <BotAvatar botId={botId} img={bot.img} small />
                <div className="flex flex-col flex-1 ml-3 text-white">
                  <div className="text-xl font-bold flex items-center">
                    {bot?.name}
                    <IonButton
                      color="primary"
                      size="small"
                      className="px-2"
                      onClick={() => setPage(2)}
                    >
                      Edit
                    </IonButton>
                  </div>
                  <div>
                    <ProfitIndicator profit={bot?.profit} />
                  </div>
                </div>
              </div>

              <div className="w-full">
                <BasicStrategy bot={bot} setBot={setBot} />
                <IonButton
                  expand="block"
                  fill="solid"
                  color="primary"
                  className="mx-6 mt-4 mb-12 h-12 normal-case"
                  onClick={() =>
                    botId || mode === "quest" ? updateBotFn() : createBot()
                  }
                >
                  {botId || mode === "quest" ? "Update" : "Create"} Bot
                </IonButton>
              </div>
            </>
          )}

          {(bot?.mode === "backtest" && !lobbyId ? [1, 2, 3] : [1, 2]).includes(
            page
          ) && (
            <div className="w-full mt-auto bottom-0">
              <IonButton
                expand="block"
                fill="solid"
                color="primary"
                className="mx-6 my-4 h-12 normal-case"
                onClick={() => setPage(page + 1)}
                disabled={
                  (page === 2 && !bot?.name) ||
                  (page === 2 && bot?.mode === "live" && !bot?.binanceSecrets)
                }
              >
                Continue
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                className="mx-6 my-4 h-12 normal-case"
                onClick={() => onDismiss()}
              >
                Cancel
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
